import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { setCartItem, removeCartItem } from "../../redux/slices/cartSlice";
import { Trash2 } from "lucide-react";

const Cart = () => {
  const { cartItems } = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const total = cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  const updateQty = (item, newQty) => {
    if (newQty < 1 || newQty > item.stock) return;
    dispatch(
      setCartItem({
        product: item.product,
        name: item.name,
        price: item.price,
        image: item.image,
        stock: item.stock,
        quantity: newQty,
      })
    );
  };

  const checkoutHandler = () => {
    navigate("/shipping");
  };

  return (
    <>
      <Helmet>
        <title>Your Bag — Sanguine Accent</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#f5f3ea] pt-28 md:pt-44 pb-20 px-4 sm:px-6 font-sourcesans">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-playfair text-[#1c1917] mb-8">
            Your Bag
            {cartItems.length > 0 && (
              <span className="ml-2 text-base font-sourcesans text-stone-400 font-normal">
                ({cartItems.length} {cartItems.length === 1 ? "item" : "items"})
              </span>
            )}
          </h1>

          {cartItems.length === 0 ? (
            <div className="text-center py-20 border border-stone-200 rounded-2xl bg-white/60">
              <p className="text-stone-500 text-lg mb-4">Your bag is empty.</p>
              <Link
                to="/supportUkraine"
                className="inline-block px-6 py-3 bg-[#BC3F4A] text-white rounded-md font-playfair text-sm hover:bg-[#a3323b] transition"
              >
                Browse the shop
              </Link>
            </div>
          ) : (
            <>
              {/* Cart items */}
              <ul className="space-y-3">
                {cartItems.map((item) => (
                  <li
                    key={item.product}
                    className="flex gap-4 p-4 bg-white rounded-xl border border-stone-200 shadow-sm"
                  >
                    {/* Image */}
                    <Link to={`/products/${item.product}`} className="shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg border border-stone-100"
                      />
                    </Link>

                    {/* Details */}
                    <div className="flex flex-1 flex-col justify-between min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={`/products/${item.product}`}
                          className="font-playfair text-[#1c1917] text-sm md:text-base hover:text-[#BC3F4A] transition-colors line-clamp-2"
                        >
                          {item.name}
                        </Link>
                        <button
                          onClick={() => dispatch(removeCartItem(item.product))}
                          aria-label={`Remove ${item.name} from bag`}
                          className="shrink-0 p-1.5 text-stone-400 hover:text-red-500 transition-colors rounded-md hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between mt-3">
                        {/* Quantity controls */}
                        <div className="flex items-center border border-stone-300 rounded-md overflow-hidden">
                          <button
                            onClick={() => updateQty(item, item.quantity - 1)}
                            disabled={item.quantity <= 1}
                            aria-label="Decrease quantity"
                            className="px-3 py-1.5 text-stone-600 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            −
                          </button>
                          <span className="px-3 py-1.5 text-sm text-[#1c1917] border-x border-stone-300 min-w-[2.5rem] text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQty(item, item.quantity + 1)}
                            disabled={item.quantity >= item.stock}
                            aria-label="Increase quantity"
                            className="px-3 py-1.5 text-stone-600 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm"
                          >
                            +
                          </button>
                        </div>

                        {/* Line total */}
                        <p className="font-playfair text-[#1c1917] text-sm md:text-base">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>

                      {/* Stock warning */}
                      {item.quantity >= item.stock && (
                        <p className="text-xs text-amber-600 mt-1">
                          Max stock reached
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {/* Order summary */}
              <div className="mt-6 p-5 bg-white rounded-xl border border-stone-200 shadow-sm space-y-3">
                <div className="flex justify-between items-center text-sm text-stone-500">
                  <span>Subtotal</span>
                  <span>${total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-stone-500">
                  <span>Shipping</span>
                  <span className="text-stone-400 italic">Calculated at checkout</span>
                </div>
                <div className="border-t border-stone-200 pt-3 flex justify-between items-center">
                  <span className="font-playfair text-[#1c1917] text-lg">Total</span>
                  <span className="font-playfair text-[#BC3F4A] text-xl">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Ukraine mission note */}
              <p className="mt-4 text-center text-xs text-stone-400 font-sourcesans">
                70% of your purchase goes directly to people serving in Ukraine.
              </p>

              {/* Checkout */}
              <div className="mt-6">
                <button
                  onClick={checkoutHandler}
                  className="w-full py-3 bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair text-base rounded-md transition focus:outline-none focus:ring-2 focus:ring-[#BC3F4A] focus:ring-offset-2"
                >
                  Checkout Securely
                </button>
                <Link
                  to="/supportUkraine"
                  className="block text-center mt-3 text-sm text-stone-500 hover:text-[#BC3F4A] transition-colors font-sourcesans"
                >
                  ← Continue shopping
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;